/**
 * Sharp's drie procesniveaus, en wat nesten ertussen betekent.
 *
 * Alec Sharp kent Process Area (0), Business Process (1) en Subprocess (2).
 * Alle drie zijn processen; het niveau zit in het stereotype. Zie §7b5 van
 * `architectuur-editor-dom.md`.
 *
 * **Waarom aggregatie en geen compositie.** Bij compositie kan het deel niet
 * bestaan zonder het geheel en hoort het bij precies één geheel. Een subproces
 * is een proces op zichzelf: haal het bedrijfsproces weg en het subproces
 * houdt niet op te bestaan, en hetzelfde subproces kan in meer dan één
 * bedrijfsproces voorkomen. Compositie zou dat tweede onmogelijk maken en het
 * eerste beweren. Aggregatie zegt precies wat er is: een groepering.
 *
 * De modelleur kan er handmatig alsnog een compositie van maken waar de
 * afhankelijkheid wél existentieel is — dit is de standaard, niet de wet.
 */

/** Stereotype-id per niveau. De volgorde ís het niveau. */
export const SHARP_PROCESS_LEVELS = [
  "sharp.ProcessArea",
  "sharp.BusinessProcess",
  "sharp.Subprocess",
] as const;

/** De relatie die het nesten van twee procesniveaus aanlegt. */
export const SHARP_NESTING_RELATION = "archimate.Aggregation";

/**
 * Het niveau van een concept, of `null` wanneer het geen procesniveau draagt.
 * Draagt iets meerdere niveaus — wat niet hoort maar kan — dan telt het
 * hoogste (laagste getal), want dat is het grofste wat het beweert.
 */
export function sharpProcessLevel(stereotypes: readonly string[] | undefined): number | null {
  if (!stereotypes || stereotypes.length === 0) return null;
  let gevonden: number | null = null;
  for (const st of stereotypes) {
    const i = (SHARP_PROCESS_LEVELS as readonly string[]).indexOf(st);
    if (i === -1) continue;
    if (gevonden === null || i < gevonden) gevonden = i;
  }
  return gevonden;
}

export type NestVerdict =
  /** Geen twee procesniveaus — dit gaat ons niet aan, laat de gewone vraag staan. */
  | { soort: "niet_van_toepassing" }
  /** Leg de aggregatie zwijgend aan. */
  | { soort: "aggregeer" }
  /** Leg 'm aan, maar zeg dat de niveaus omgekeerd staan. */
  | { soort: "aggregeer_met_waarschuwing"; kindNiveau: number; ouderNiveau: number };

/**
 * Wat er moet gebeuren als `kind` in `ouder` genest wordt.
 *
 * - Omlaag (gebied ⊃ proces ⊃ subproces) is de normale beweging.
 * - **Gelijk niveau mag.** Sharp's niveaus zijn relatief en een groot domein
 *   heeft soms een tussenlaag nodig; dat blokkeren zou het gereedschap de
 *   methode laten overrulen.
 * - **Omhoog is een niveaufout** — een procesgebied binnen een subproces. We
 *   weigeren 'm niet (de taal staat de relatie toe, en de matrix is de harde
 *   grens, niet de methode) maar we zeggen het wel.
 */
export function sharpNestVerdict(
  kindStereotypes: readonly string[] | undefined,
  ouderStereotypes: readonly string[] | undefined,
): NestVerdict {
  const kind = sharpProcessLevel(kindStereotypes);
  const ouder = sharpProcessLevel(ouderStereotypes);
  if (kind === null || ouder === null) return { soort: "niet_van_toepassing" };
  if (kind < ouder) {
    return { soort: "aggregeer_met_waarschuwing", kindNiveau: kind, ouderNiveau: ouder };
  }
  return { soort: "aggregeer" };
}
